import React, { useEffect } from "react";

import { Col, Container, Form, Row } from "react-bootstrap";

import { useForm } from "react-hook-form";

import { AcquiredDateChoices, ResaleDataInput, ResaleInput } from "@api/graphql";
import { useAuthenticationContext } from "@context/authentication";
import { getDisplayValueForAcquiredDateChoices, isCMO } from "@util/index";

import { FormErrorText } from "../form/FormErrorText";
import FormNavigationButtons from "../form/FormNavigationButtons";
import FormsHeader from "../form/FormsHeader";
import { FormStepProps } from "../form/MultiStepForm";

import styles from "./ResaleDetails.module.scss";

interface ResaleDetailsProps extends FormStepProps<ResaleInput, ResaleDataInput> {
  isInsertMode?: boolean;
  isEditable?: boolean;
  submitRef: React.RefObject<HTMLButtonElement>;
  onValidationStatus?: (isValid: boolean) => void;
}

export const ResaleDetails = ({
  initialFormData,
  onCancel,
  onSubmit,
  isInsertMode = true,
  isEditable = true,
  submitRef,
  onValidationStatus,
}: ResaleDetailsProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResaleDataInput>({ defaultValues: initialFormData });

  const { auth } = useAuthenticationContext();
  const userParticipantType = auth?.currentUserData?.participant?.participant_types;
  const isSalePriceRequired = !isCMO(userParticipantType);

  useEffect(() => {
    if (onValidationStatus) {
      onValidationStatus(isValid);
    }
  }, [isValid, onValidationStatus]);

  const handleCancel = () => onCancel && onCancel();

  return (
    <Form>
      <Container fluid>
        <FormsHeader isInsertMode={isInsertMode} title="Resale details" />
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="acquireDate">
              <Form.Label>* Did the reseller acquire this work after 8th June 2010?</Form.Label>
              <Row className="mb-3">
                <Col className="mb-1" sm={6}>
                  <Form.Check
                    {...register("acquired_date_choice", { required: true })}
                    label={getDisplayValueForAcquiredDateChoices(AcquiredDateChoices.YES)}
                    value={AcquiredDateChoices.YES}
                    type="radio"
                    id={`inline-radio-yes`}
                    readOnly={!isEditable}
                    className={!isEditable ? styles["form-disabled"] : ""}
                  />
                </Col>
                <Col className="mb-1" sm={6}>
                  <Form.Check
                    {...register("acquired_date_choice")}
                    label={getDisplayValueForAcquiredDateChoices(AcquiredDateChoices.NO)}
                    value={AcquiredDateChoices.NO}
                    type="radio"
                    id={`inline-radio-no`}
                    readOnly={!isEditable}
                    className={!isEditable ? styles["form-disabled"] : ""}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="mb-1" sm={6}>
                  <Form.Check
                    {...register("acquired_date_choice")}
                    label={getDisplayValueForAcquiredDateChoices(AcquiredDateChoices.PRIMARY_SALE)}
                    value={AcquiredDateChoices.PRIMARY_SALE}
                    type="radio"
                    id={`inline-radio-primary`}
                    readOnly={!isEditable}
                    className={!isEditable ? styles["form-disabled"] : ""}
                  />
                </Col>
                <Col className="mb-1" sm={6}>
                  <Form.Check
                    {...register("acquired_date_choice")}
                    label={getDisplayValueForAcquiredDateChoices(AcquiredDateChoices.VOLUNTARY)}
                    value={AcquiredDateChoices.VOLUNTARY}
                    type="radio"
                    id={`inline-radio-voluntary`}
                    readOnly={!isEditable}
                    className={!isEditable ? styles["form-disabled"] : ""}
                  />
                </Col>
              </Row>
              <FormErrorText
                error={errors.acquired_date_choice}
                message="Please select an option from above."
              ></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="resalePrice">
              <Form.Label>{isSalePriceRequired && "*"} Resale price (inc. GST)</Form.Label>
              <Form.Control
                {...register("artwork_sale_price", {
                  required: isSalePriceRequired,
                  pattern: /^\d{1,10}(\.\d{1,2})?$/,
                })}
                type="text"
                placeholder="Enter resale price"
                disabled={!isEditable}
                className={!isEditable ? styles["form-disabled"] : ""}
              />
              <FormErrorText
                error={errors.artwork_sale_price}
                message="Please enter a price of a numeric value, 10 figures, 2 decimal places."
              ></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="soldDate">
              <Form.Label>* Date sold</Form.Label>
              <Form.Control
                {...register("artwork_sale_date", {
                  required: true,
                })}
                type="date"
                disabled={!isEditable}
                className={!isEditable ? styles["form-disabled"] : ""}
              />
              <FormErrorText error={errors.artwork_sale_date} message="Date should be selected."></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <FormNavigationButtons
          isInsertMode={isInsertMode}
          onSubmit={onSubmit}
          onCancel={handleCancel}
          handleSubmit={handleSubmit}
          submitRef={submitRef}
        />
      </Container>
    </Form>
  );
};
